import axios from "axios";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { LOG } from "../utils/logUtil";
import { useAppLoading } from "./useAppLoading";
import { useCurrentLocation } from "./useCurrentLocation";

interface IRegisteredUsersStats {
  registeredUsersByDay: {
    [key: string]: number;
  };
  registeredUsersByWeek: {
    [key: string]: number;
  };
  registeredUsersByMonth: {
    [key: string]: number;
  };
  registeredUsersByYear: {
    [key: string]: number;
  };
}

interface FromToParams {
  period: "day" | "month" | "week" | "quarter" | "year";
  startYear: number;
  startMonth: number;
  endYear: number;
  endMonth: number;
  startWeek?: number;
  endWeek?: number;
  startQuarter?: number;
  endQuarter?: number;
}

interface GETUserStatsProps {
  period:
    | "all"
    | "yearly"
    | "quarterly"
    | "monthly"
    | "weekly"
    | "month"
    | "week"
    | "quarter"
    | "year";
  year?: number;
  month?: number;
  quarter?: "Q1" | "Q2" | "Q3" | "Q4";
  week?: Date;
  selectedFilters?: string[];
  selectedStores?: string[];
  fromToParams?: FromToParams;
  /** When true, do not filter by location - show all data */
  ignoreLocation?: boolean;
}

interface GETUserChurnProps {
  startYear: number;
  startMonth: number;
  endYear: number;
  endMonth: number;
  selectedFilters?: string[];
  selectedStores?: string[];
}

export interface DroppersPerStoreResponse {
  categories: string[];
  series: { name: string; data: number[] }[];
}

export interface DropsPerUserPerLocationResponse {
  categories: string[];
  series: { name: string; data: number[] }[];
}

interface IStore {
  registeredUserStats: IRegisteredUsersStats | null;
  isRegisteredUserStatsLoading: boolean;
  droppersPerStoreOverTime: DroppersPerStoreResponse | null;
  isDroppersPerStoreLoading: boolean;
  isDroppersPerStoreError: boolean;
  dropsPerUserPerLocation: DropsPerUserPerLocationResponse | null;
  isDropsPerUserPerLocationLoading: boolean;
  isDropsPerUserPerLocationError: boolean;
  activeUserStats: any;
  userStats: {
    allDownloads: number;
    regularDroppers: number;
    activeDroppers: number;
    droppers: number;
    inactiveDroppers: number;
  } | null;
  isCumulativeUsersOverTimeLoading: boolean;
  cumulativeUsersOverTime: any;
  isNonCumulativeUsersOverTimeLoading: boolean;
  nonCumulativeUsersOverTime: any;
  isActiveInactiveUsersLoading: boolean;
  activeInactiveUsers: any;
  isCumulativeChurnLoading: boolean;
  cumulativeChurn: any;
  isNonCumulativeChurnLoading: boolean;
  nonCumulativeChurn: any;
  isCumulativeUsersConversionLoading: boolean;
  cumulativeUsersConversion: any;
  isNonCumulativeUsersConversionLoading: boolean;
  nonCumulativeUsersConversion: {
    result: Record<string, number[]>;
    allResult: Record<string, number[]>;
  } | null;
  getActiveUserStats: (
    requestedDate: Date,
    period: "daily" | "weekly" | "monthly" | "yearly"
  ) => void;
  getRegisteredUserStats: (
    requestedYear?: number,
    requestedDay?: Date,
    fromToParams?: {
      period: "day" | "month" | "week" | "quarter" | "year";
      startYear: number;
      startMonth: number;
      endYear: number;
      endMonth: number;
    }
  ) => void;
  getDroppersPerStoreOverTime: (
    fromToParams: FromToParams & {
      materialId?: string;
      metric?: "droppers" | "regular" | "5plus";
    }
  ) => void;
  getDropsPerUserPerLocation: (
    fromToParams: FromToParams & {
      cities?: string[];
      dimension?: "city" | "location";
    }
  ) => void;
  getUserStats: ({
    period,
    year,
    month,
    quarter,
    week,
    selectedFilters,
    selectedStores,
  }: GETUserStatsProps) => void;
  getCumulativeUsersOverTime: ({
    period,
    year,
    month,
    quarter,
    week,
    selectedFilters,
    selectedStores,
  }: GETUserStatsProps) => void;
  getNonCumulativeUsersOverTime: ({
    period,
    year,
    month,
    quarter,
    week,
    selectedFilters,
    selectedStores,
  }: GETUserStatsProps) => void;
  getActiveInactiveUsers: ({
    period,
    year,
    month,
    quarter,
    week,
    selectedFilters,
    selectedStores,
  }: GETUserStatsProps) => void;
  getCumulativeChurn: ({
    period,
    year,
    month,
    quarter,
    week,
    selectedFilters,
    selectedStores,
  }: GETUserStatsProps) => void;
  getNonCumulativeChurn: ({
    startYear,
    startMonth,
    endYear,
    endMonth,
    selectedFilters,
    selectedStores,
  }: GETUserChurnProps) => void;
  getCumulativeUsersConversion: ({
    period,
    year,
    month,
    quarter,
    week,
    selectedFilters,
    selectedStores,
  }: GETUserStatsProps) => void;
  getNonCumulativeUsersConversion: ({
    period,
    year,
    month,
    quarter,
    week,
    selectedFilters,
    selectedStores,
  }: GETUserStatsProps) => void;
}

export const useUserStats = create<IStore>()(
  persist(
    (set) => ({
      registeredUserStats: null,
      isRegisteredUserStatsLoading: false,
      droppersPerStoreOverTime: null,
      isDroppersPerStoreLoading: false,
      isDroppersPerStoreError: false,
      dropsPerUserPerLocation: null,
      isDropsPerUserPerLocationLoading: false,
      isDropsPerUserPerLocationError: false,
      activeUserStats: null,
      userStats: null,
      isCumulativeUsersOverTimeLoading: false,
      cumulativeUsersOverTime: null,
      isNonCumulativeUsersOverTimeLoading: false,
      nonCumulativeUsersOverTime: null,
      isActiveInactiveUsersLoading: false,
      activeInactiveUsers: null,
      isCumulativeChurnLoading: false,
      cumulativeChurn: null,
      isNonCumulativeChurnLoading: false,
      nonCumulativeChurn: null,
      isCumulativeUsersConversionLoading: false,
      cumulativeUsersConversion: null,
      isNonCumulativeUsersConversionLoading: false,
      nonCumulativeUsersConversion: null,
      getActiveUserStats: async (
        requestedDate = new Date(),
        period = "monthly"
      ) => {
        useAppLoading.getState().setIsLoading(true);
        const currentLocation = useCurrentLocation.getState().currentLocation;
        try {
          const response = await axios.get(
            `/users/stats/active-users?requestedDate=${requestedDate}&period=${period}&location=${currentLocation}`
          );
          if (response?.data) {
            set({ activeUserStats: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching active user stats", { error });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getRegisteredUserStats: async (
        requestedYear,
        requestedDay = new Date(),
        fromToParams?
      ) => {
        if (fromToParams) {
          set({ isRegisteredUserStatsLoading: true });
        } else {
          useAppLoading.getState().setIsLoading(true);
        }
        try {
          let url = "/users/registered-stats?";
          if (fromToParams) {
            url += `startYear=${fromToParams.startYear}&startMonth=${fromToParams.startMonth}&endYear=${fromToParams.endYear}&endMonth=${fromToParams.endMonth}&period=${fromToParams.period}`;
          } else {
            url += `requestedYear=${requestedYear}&requestedDay=${requestedDay}`;
          }
          const response = await axios.get(url);
          if (response?.data) {
            set({ registeredUserStats: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching regstered user stats", { error });
        } finally {
          if (fromToParams) {
            set({ isRegisteredUserStatsLoading: false });
          } else {
            useAppLoading.getState().setIsLoading(false);
          }
        }
      },
      getDroppersPerStoreOverTime: async (
        fromToParams: FromToParams & {
          materialId?: string;
          metric?: "droppers" | "regular" | "5plus";
        }
      ) => {
        set({
          isDroppersPerStoreLoading: true,
          isDroppersPerStoreError: false,
        });
        try {
          let url = `/users/droppers-per-store?startYear=${fromToParams.startYear}&startMonth=${fromToParams.startMonth}&endYear=${fromToParams.endYear}&endMonth=${fromToParams.endMonth}&period=${fromToParams.period}`;
          if (fromToParams.materialId) {
            url += `&materialId=${fromToParams.materialId}`;
          }
          if (fromToParams.metric) {
            url += `&metric=${fromToParams.metric}`;
          }
          const response = await axios.get(url);
          if (response?.data) {
            set({ droppersPerStoreOverTime: response.data });
          }
        } catch (error) {
          LOG.error("Error on fetching droppers per store", { error });
          set({
            droppersPerStoreOverTime: null,
            isDroppersPerStoreError: true,
          });
        } finally {
          set({ isDroppersPerStoreLoading: false });
        }
      },
      getDropsPerUserPerLocation: async (
        fromToParams: FromToParams & {
          cities?: string[];
          dimension?: "city" | "location";
        }
      ) => {
        set({
          isDropsPerUserPerLocationLoading: true,
          isDropsPerUserPerLocationError: false,
        });
        try {
          const params = new URLSearchParams({
            startYear: fromToParams.startYear.toString(),
            startMonth: fromToParams.startMonth.toString(),
            endYear: fromToParams.endYear.toString(),
            endMonth: fromToParams.endMonth.toString(),
            period: fromToParams.period,
            dimension: (fromToParams.dimension ?? "city").toString(),
          });
          if (fromToParams.cities?.length) {
            params.append("cities", fromToParams.cities.join(","));
          }
          const response = await axios.get(
            `/users/drops-per-user-per-location?${params.toString()}`
          );
          if (response?.data) {
            set({ dropsPerUserPerLocation: response.data });
          }
        } catch (error) {
          LOG.error("Error on fetching drops per user per location", { error });
          set({
            dropsPerUserPerLocation: null,
            isDropsPerUserPerLocationError: true,
          });
        } finally {
          set({ isDropsPerUserPerLocationLoading: false });
        }
      },
      getUserStats: async ({
        period,
        year,
        month,
        quarter,
        week,
        selectedFilters,
        selectedStores,
        ignoreLocation,
      }: GETUserStatsProps) => {
        useAppLoading.getState().setIsLoading(true);
        const currentLocation = useCurrentLocation.getState().currentLocation;

        // Build query parameters
        const params = new URLSearchParams();
        params.append("period", period);
        if (year !== undefined) {
          params.append("year", year.toString());
        }
        if (month !== undefined) {
          params.append("month", month.toString());
        }
        if (quarter !== undefined) {
          params.append("quarter", quarter);
        }
        if (week !== undefined) {
          params.append("week", week.toISOString());
        }
        if (!ignoreLocation && currentLocation) {
          params.append("location", currentLocation);
        }
        if (
          selectedFilters !== undefined &&
          selectedFilters?.length > 0 &&
          !selectedFilters?.includes("all")
        ) {
          params.append("selectedStreams", selectedFilters.join(","));
        }
        if (
          selectedStores &&
          selectedStores.length > 0 &&
          !selectedStores.includes("all")
        ) {
          params.append("selectedStores", selectedStores.join(","));
        }

        try {
          const response = await axios.get(
            `/users/new-stats/general?${params.toString()}`
          );
          if (response?.data) {
            set({ userStats: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching user stats", { error });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getCumulativeUsersOverTime: async ({
        period,
        year,
        month,
        quarter,
        week,
        selectedFilters,
        selectedStores,
        fromToParams,
        ignoreLocation,
      }: GETUserStatsProps) => {
        set({ isCumulativeUsersOverTimeLoading: true });
        const currentLocation = useCurrentLocation.getState().currentLocation;

        // Build query parameters
        const params = new URLSearchParams();
        params.append("period", fromToParams ? fromToParams.period : period);
        if (fromToParams) {
          params.append("startYear", fromToParams.startYear.toString());
          params.append("startMonth", fromToParams.startMonth.toString());
          params.append("endYear", fromToParams.endYear.toString());
          params.append("endMonth", fromToParams.endMonth.toString());
        } else if (year !== undefined) {
          params.append("year", year.toString());
        }
        if (month !== undefined && !fromToParams) {
          params.append("month", month.toString());
        }
        if (quarter !== undefined && !fromToParams) {
          params.append("quarter", quarter);
        }
        if (week !== undefined && !fromToParams) {
          params.append("week", week.toISOString());
        }
        if (!ignoreLocation && currentLocation) {
          params.append("location", currentLocation);
        }
        if (
          selectedFilters !== undefined &&
          selectedFilters?.length > 0 &&
          !selectedFilters?.includes("all")
        ) {
          params.append("selectedStreams", selectedFilters.join(","));
        }
        if (
          selectedStores &&
          selectedStores.length > 0 &&
          !selectedStores.includes("all")
        ) {
          params.append("selectedStores", selectedStores.join(","));
        }

        try {
          const response = await axios.get(
            `/users/new-stats/cumulative-users-over-time?${params.toString()}`
          );
          if (response?.data) {
            set({ cumulativeUsersOverTime: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching cumulative users over time", { error });
        } finally {
          set({ isCumulativeUsersOverTimeLoading: false });
        }
      },
      getNonCumulativeUsersOverTime: async ({
        period,
        year,
        month,
        quarter,
        week,
        selectedFilters,
        selectedStores,
      }: GETUserStatsProps) => {
        set({ isNonCumulativeUsersOverTimeLoading: true });
        const currentLocation = useCurrentLocation.getState().currentLocation;

        // Build query parameters
        const params = new URLSearchParams();
        params.append("period", period);
        if (year !== undefined) {
          params.append("year", year.toString());
        }
        if (month !== undefined) {
          params.append("month", month.toString());
        }
        if (quarter !== undefined) {
          params.append("quarter", quarter);
        }
        if (week !== undefined) {
          params.append("week", week.toISOString());
        }
        if (currentLocation) {
          params.append("location", currentLocation);
        }
        if (
          selectedFilters !== undefined &&
          !selectedFilters?.includes("all")
        ) {
          params.append("selectedStreams", selectedFilters.join(","));
        }
        if (
          selectedStores &&
          selectedStores.length > 0 &&
          !selectedStores.includes("all")
        ) {
          params.append("selectedStores", selectedStores.join(","));
        }

        try {
          const response = await axios.get(
            `/users/new-stats/non-cumulative-users-over-time?${params.toString()}`
          );
          if (response?.data) {
            set({ nonCumulativeUsersOverTime: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching non-cumulative users over time", {
            error,
          });
        } finally {
          set({ isNonCumulativeUsersOverTimeLoading: false });
        }
      },
      getActiveInactiveUsers: async ({
        period,
        year,
        month,
        quarter,
        week,
        selectedFilters,
        selectedStores,
      }: GETUserStatsProps) => {
        set({ isActiveInactiveUsersLoading: true });
        const currentLocation = useCurrentLocation.getState().currentLocation;

        // Build query parameters
        const params = new URLSearchParams();
        params.append("period", period);
        if (year !== undefined) {
          params.append("year", year.toString());
        }
        if (month !== undefined) {
          params.append("month", month.toString());
        }
        if (quarter !== undefined) {
          params.append("quarter", quarter);
        }
        if (week !== undefined) {
          params.append("week", week.toISOString());
        }
        if (currentLocation) {
          params.append("location", currentLocation);
        }
        if (
          selectedFilters !== undefined &&
          !selectedFilters?.includes("all")
        ) {
          params.append("selectedStreams", selectedFilters.join(","));
        }
        if (
          selectedStores &&
          selectedStores.length > 0 &&
          !selectedStores.includes("all")
        ) {
          params.append("selectedStores", selectedStores.join(","));
        }

        try {
          const response = await axios.get(
            `/users/new-stats/non-cumulative-active-inactive-users?${params.toString()}`
          );
          if (response?.data) {
            set({ activeInactiveUsers: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching active/inactive users", { error });
        } finally {
          set({ isActiveInactiveUsersLoading: false });
        }
      },

      getCumulativeChurn: async ({
        period,
        year,
        month,
        quarter,
        week,
        selectedFilters,
        selectedStores,
      }: GETUserStatsProps) => {
        set({ isCumulativeChurnLoading: true });
        const currentLocation = useCurrentLocation.getState().currentLocation;

        // Build query parameters
        const params = new URLSearchParams();
        params.append("period", period);
        if (year !== undefined) {
          params.append("year", year.toString());
        }
        if (month !== undefined) {
          params.append("month", month.toString());
        }
        if (quarter !== undefined) {
          params.append("quarter", quarter);
        }
        if (week !== undefined) {
          params.append("week", week.toISOString());
        }
        if (currentLocation) {
          params.append("location", currentLocation);
        }
        if (
          selectedFilters !== undefined &&
          !selectedFilters?.includes("all")
        ) {
          params.append("selectedStreams", selectedFilters.join(","));
        }
        if (
          selectedStores &&
          selectedStores.length > 0 &&
          !selectedStores.includes("all")
        ) {
          params.append("selectedStores", selectedStores.join(","));
        }

        try {
          const response = await axios.get(
            `/users/new-stats/cumulative-churn?${params.toString()}`
          );
          if (response?.data) {
            set({ cumulativeChurn: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching cumulative churn", { error });
        } finally {
          set({ isCumulativeChurnLoading: false });
        }
      },
      getNonCumulativeChurn: async ({
        startYear,
        startMonth,
        endYear,
        endMonth,
        selectedFilters,
        selectedStores,
      }: GETUserChurnProps) => {
        set({ isNonCumulativeChurnLoading: true });
        const currentLocation = useCurrentLocation.getState().currentLocation;

        // Build query parameters
        const params = new URLSearchParams();
        params.append("startYear", startYear.toString());
        params.append("startMonth", startMonth.toString());
        params.append("endYear", endYear.toString());
        params.append("endMonth", endMonth.toString());
        if (currentLocation) {
          params.append("location", currentLocation);
        }
        if (
          selectedFilters !== undefined &&
          !selectedFilters?.includes("all")
        ) {
          params.append("selectedStreams", selectedFilters.join(","));
        }
        if (
          selectedStores &&
          selectedStores.length > 0 &&
          !selectedStores.includes("all")
        ) {
          params.append("selectedStores", selectedStores.join(","));
        }

        try {
          const response = await axios.get(
            `/users/new-stats/user-churn-per-location?${params.toString()}`
          );
          if (response?.data) {
            set({ nonCumulativeChurn: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching non-cumulative churn", { error });
        } finally {
          set({ isNonCumulativeChurnLoading: false });
        }
      },
      getCumulativeUsersConversion: async ({
        period,
        year,
        month,
        quarter,
        week,
        selectedFilters,
        selectedStores,
      }: GETUserStatsProps) => {
        set({ isCumulativeUsersConversionLoading: true });
        const currentLocation = useCurrentLocation.getState().currentLocation;

        // Build query parameters
        const params = new URLSearchParams();
        params.append("period", period);
        if (year !== undefined) {
          params.append("year", year.toString());
        }
        if (month !== undefined) {
          params.append("month", month.toString());
        }
        if (quarter !== undefined) {
          params.append("quarter", quarter);
        }
        if (week !== undefined) {
          params.append("week", week.toISOString());
        }
        if (currentLocation) {
          params.append("location", currentLocation);
        }
        if (
          selectedFilters !== undefined &&
          !selectedFilters?.includes("all")
        ) {
          params.append("selectedStreams", selectedFilters.join(","));
        }
        if (
          selectedStores &&
          selectedStores.length > 0 &&
          !selectedStores.includes("all")
        ) {
          params.append("selectedStores", selectedStores.join(","));
        }

        try {
          const response = await axios.get(
            `/users/new-stats/cumulative-users-conversion?${params.toString()}`
          );
          if (response?.data) {
            set({ cumulativeUsersConversion: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching cumulative users conversion", { error });
        } finally {
          set({ isCumulativeUsersConversionLoading: false });
        }
      },
      getNonCumulativeUsersConversion: async ({
        period,
        year,
        month,
        quarter,
        week,
        selectedFilters,
        selectedStores,
      }: GETUserStatsProps) => {
        set({ isNonCumulativeUsersConversionLoading: true });
        const currentLocation = useCurrentLocation.getState().currentLocation;

        // Build query parameters
        const params = new URLSearchParams();
        params.append("period", period);
        if (year !== undefined) {
          params.append("year", year.toString());
        }
        if (month !== undefined) {
          params.append("month", month.toString());
        }
        if (quarter !== undefined) {
          params.append("quarter", quarter);
        }
        if (week !== undefined) {
          params.append("week", week.toISOString());
        }
        if (currentLocation) {
          params.append("location", currentLocation);
        }
        if (
          selectedFilters !== undefined &&
          !selectedFilters?.includes("all")
        ) {
          params.append("selectedStreams", selectedFilters.join(","));
        }
        if (
          selectedStores &&
          selectedStores.length > 0 &&
          !selectedStores.includes("all")
        ) {
          params.append("selectedStores", selectedStores.join(","));
        }

        try {
          const response = await axios.get(
            `/users/new-stats/non-cumulative-users-conversion?${params.toString()}`
          );
          if (response?.data) {
            set({ nonCumulativeUsersConversion: response?.data });
          }
        } catch (error) {
          LOG.error("Error on fetching non-cumulative users conversion", {
            error,
          });
        } finally {
          set({ isNonCumulativeUsersConversionLoading: false });
        }
      },
    }),
    {
      name: "user-stats-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(
            ([k]) =>
              !k.endsWith("Loading") &&
              ![
                "droppersPerStoreOverTime",
                "dropsPerUserPerLocation",
                "registeredUserStats",
                "cumulativeUsersOverTime",
              ].includes(k)
          )
        ) as typeof state,
    }
  )
);
